import type { Role } from '@time-tracking/shared';
import { useId, useState } from 'react';

import { ROLE_LABELS } from '../../auth/access.js';
import { buildInfo } from '../../build-info.js';
import { AlertTriangleIcon } from '../../components/icons/AlertTriangleIcon.js';
import { ExternalLinkIcon } from '../../components/icons/ExternalLinkIcon.js';
import { MailIcon } from '../../components/icons/MailIcon.js';
import { UsersIcon } from '../../components/icons/UsersIcon.js';
import { WrenchIcon } from '../../components/icons/WrenchIcon.js';
import { buildReportLink, REPORT_SOURCE_PAGE } from './report-url.js';

/**
 * "Something looks wrong" — the two doors.
 *
 * PROTOTYPE OF RECORD: `design_handoff_help_faq/` — `03-something-looks-wrong.png` and
 * `04-door-a-expanded-contributor.png`. The README calls this section's copy spec rather
 * than placeholder, so every string here is transcribed, not paraphrased.
 *
 * THIS SECTION CARRIES THE PRIVACY DECISION. One door leads to a person and creates no
 * record; the other publishes to a tracker every colleague in the department can read. A
 * user has to be able to tell in about a second which one is theirs, so each card states
 * both the *kind* of problem and *what happens next* in its kicker, above the heading —
 * and the examples under the lede do most of the routing work, because a person matches
 * their situation to an example faster than to a category name.
 *
 * The consequence of getting it wrong is asymmetric: a defect filed to an approver costs
 * one redirect, while a payroll dispute filed to the issue tracker cannot be unpublished.
 * That asymmetry is why Door A creates no ticket at all rather than offering a
 * "sensitive?" checkbox on the way to one.
 */
export function Doors({ role }: { readonly role: Role | undefined }) {
  return (
    <div className="help-doors">
      <DataDoor />
      <DefectDoor role={role} />
    </div>
  );
}

/**
 * Door A — a data problem. Hours, assignments, approvals: fixed by a person.
 *
 * TAKES NO ROLE, ON PURPOSE. The prototype branches this panel's guidance on whether the
 * viewer can open Projects & tasks, sending approvers and administrators to "look at the
 * Approvers line" on their team. There is no such line: `ProjectsPage` scopes non-admins
 * to teams where they *already* hold an approver role, and the only rendered "Approvers"
 * label in the app is inside the administrator-only Teams pane. An instruction that sends
 * someone to a page which cannot answer them ends with them falling through to Door B and
 * publishing their hours — the exact failure this split exists to prevent, so one correct
 * instruction beats two where one is a dead end.
 *
 * Not "role is unused here": role is not *plumbed* here, which is the point. A prop this
 * component ignores would invite the branch back. Revisit when a read path exposing a
 * team's approvers to its members exists (deferred in the plan, and the handoff calls its
 * own "start with your manager" copy a stopgap for the same reason).
 */
function DataDoor() {
  const [guidanceOpen, setGuidanceOpen] = useState(false);
  const panelId = useId();

  return (
    <div className="help-door help-door--data">
      {/* A div, not a heading or a paragraph: it is a two-part label sitting above the
          h3 that names this card, and the h3 is what belongs in the heading outline. A
          div also keeps it out of `.help-door > p`'s lede treatment. */}
      <div className="help-door-kicker">
        <UsersIcon /> Your data · a person fixes it
      </div>
      <h3>My hours or assignments are wrong</h3>
      <p>
        Hours, project assignments, and approvals are managed by your team&apos;s approver. They can
        return a week to you, correct an assignment, or undo an approval.
      </p>
      {/* `role="list"` is not redundant despite what jsx-a11y assumes: `list-style: none`
          on this ul suppresses the implicit ARIA list mapping in WebKit/VoiceOver, so the
          role is the only way "list, 3 items" is still announced. The rule's premise is
          that the role duplicates the element's semantics — true until the CSS removes
          them. */}
      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
      <ul className="help-door-examples" role="list">
        <li>Hours saved on the wrong day or the wrong project</li>
        <li>A project missing from your timesheet</li>
        <li>A week approved before you were finished with it</li>
      </ul>
      <p>
        Say which week it is, which project row is wrong, and what it should say instead — that is
        usually enough for them to fix it in one pass.
      </p>
      <div className="help-door-foot">
        {/* A disclosure, not a link: there is nowhere in the app to send them, and an
            anchor here is the one thing that would make this door look like a filing
            path. Real <button> + aria-expanded + aria-controls, and the panel is
            unmounted when closed — same shape as UsersPane's sync-settings toggle. */}
        <button
          type="button"
          className="btn btn--secondary"
          aria-expanded={guidanceOpen}
          aria-controls={panelId}
          onClick={() => {
            setGuidanceOpen((open) => !open);
          }}
        >
          <MailIcon /> How to reach your approver
        </button>
        {guidanceOpen && (
          <div className="help-door-reach" id={panelId}>
            <p>
              <strong>Start with your manager.</strong> They are usually the person who approves
              your timesheet, and they can return a week to you or fix an assignment. If they are
              not, they will know who is.
            </p>
            <p>
              Email or message them with the week, the project row, and what it should say instead.
            </p>
          </div>
        )}
        {/* States the absence of a ticket as a reason rather than leaving it as a gap —
            otherwise Door B reads as the more complete path and wins by default. */}
        <p className="help-door-note">
          Nothing about your hours belongs in a public report, so this path doesn&apos;t create one.
        </p>
      </div>
    </div>
  );
}

/**
 * Door B — a software defect. Opens the GitHub defect form, prefilled with build
 * diagnostics and nothing else.
 *
 * @param role Resolved role, or `undefined` while `/v1/me` is pending or has failed. Both
 *   of those render neutrally: the role field is omitted from the URL and its chip from
 *   the receipt, rather than being defaulted. An approver who sees "Contributor" in the
 *   receipt for two frames learns that the receipt is decorative, and the receipt is the
 *   only assurance on this page about what leaves the app.
 */
function DefectDoor({ role }: { readonly role: Role | undefined }) {
  const receiptId = useId();
  const warnId = useId();

  // One call, two consumers: `href` below and `chips` in the receipt. `buildReportLink`
  // returns both so the receipt cannot describe a URL other than the one it sits under.
  const report = buildReportLink({
    template: 'defect',
    diagnostics: {
      environment: buildInfo.environment,
      version: buildInfo.version,
      page: REPORT_SOURCE_PAGE,
      role: role === undefined ? undefined : ROLE_LABELS[role],
    },
  });

  return (
    <div className="help-door help-door--defect">
      <div className="help-door-kicker">
        <WrenchIcon /> The software · a fix gets tracked
      </div>
      <h3>The app itself is broken</h3>
      <p>
        A button that does nothing, a page that won&apos;t load, a total that computes wrong. These
        become tracked defect reports.
      </p>
      {/* `role="list"` is not redundant despite what jsx-a11y assumes: `list-style: none`
          on this ul suppresses the implicit ARIA list mapping in WebKit/VoiceOver, so the
          role is the only way "list, 3 items" is still announced. The rule's premise is
          that the role duplicates the element's semantics — true until the CSS removes
          them. */}
      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
      <ul className="help-door-examples" role="list">
        <li>An action fails or does nothing at all</li>
        <li>A page won&apos;t load or shows an error</li>
        <li>A number is wrong even though the entries are right</li>
      </ul>
      {/* ABOVE THE BUTTON, NOT BELOW IT (R8, AE6). A warning read after the click is a
          warning about something already published. Colour never carries it alone: the
          icon and the title text say "visible to every colleague" in words. Not a live
          region — it is present from first paint and announces in reading order. */}
      <div className="help-door-warn" id={warnId}>
        <AlertTriangleIcon className="help-door-warn-icon" />
        <div>
          {/* Not a heading: a one-line lead-in to the sentence under it. A heading here
              would inject an h4 between the card's h3 and nothing. */}
          <div className="help-door-warn-title">Reports are visible to every colleague</div>
          <p>
            Repository access is shared across the department. Remove hours, names, and EIDs from
            any screenshot before you attach it.
          </p>
        </div>
      </div>
      <div className="help-door-foot">
        {/* `rel="noopener noreferrer"` with `target="_blank"`: noopener denies the opened
            tab a handle on this window, noreferrer keeps the app's URL out of GitHub's
            referrer. The "opens in a new tab" affordance is visible in the receipt label
            below. `aria-describedby` names the WARNING FIRST and then the receipt, so a
            screen-reader user hears what is at stake AND what the link carries before
            activating — the same guarantee the sighted reader gets from reading downward.
            The warning must be in here, not merely above in DOM order: a user working a
            links list (Insert+F7, or the VoiceOver rotor) reaches this link directly and
            would otherwise activate it having never heard the warning at all. It is the
            one thing standing between a reporter and publishing their hours, so it does
            not get to be the part only linear readers receive. Accepted cost: linear
            readers now hear the warning twice, once as the description and once as flow
            content. That is not a failure, and the warning is worth it. */}
        <a
          className="btn btn--primary"
          href={report.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-describedby={`${warnId} ${receiptId}`}
        >
          Report a problem <ExternalLinkIcon />
        </a>
        <div className="help-door-prefill" id={receiptId}>
          <div className="help-door-prefill-label">Opens GitHub in a new tab, filled in with:</div>
          <div className="help-door-chips">
            {report.chips.map((chip) => (
              // Keyed by field id, not by value: two diagnostics can legitimately hold
              // the same string (both falling back to the build-info sentinel, say).
              <span className="help-door-chip" key={chip.field}>
                {chip.value}
              </span>
            ))}
          </div>
        </div>
        {/* COPY CORRECTED AGAINST WHAT THE URL ACTUALLY SENDS (divergence 16). The
            prototype's footnote reads "…only what you type", which contradicts the chips
            immediately above it: environment, version, page and (when known) role all
            travel with the link BEFORE the reporter types anything. On a page whose entire
            purpose is being honest about what becomes visible to the department, a privacy
            statement that undersells what leaves the app is the worst possible line to get
            wrong.

            NO COUNT IN THE SENTENCE. The first correction said "the four details above",
            which is itself false on the degraded path: `buildReportLink` omits the role
            when `/v1/me` has failed, so only three chips render. Fixing one inaccuracy by
            introducing another is not a fix, and Copilot caught it. Deriving the number
            from `report.chips.length` would work, but a sentence with no count cannot drift
            out of step with the list at all — and the reader can see the chips. The
            reassurance the line exists to give, no identity and no hours, is unchanged. */}
        <p className="help-door-note">
          No name, EID, or timesheet value is sent — only the details above and what you type.
        </p>
      </div>
    </div>
  );
}
